// fallow-ignore-file code-duplication
import {
	APP_INITIALIZER,
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	provideBrowserGlobalErrorListeners,
	provideZonelessChangeDetection,
} from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG, OverlayContainer } from '@angular/cdk/overlay';
import { provideHttpClient, withXhr } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
	provideRouter,
	Router,
	RouteReuseStrategy,
	withPreloading,
	withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { CubeClientModule } from '@cubejs-client/ngx';
import { EffectsModule } from '@ngrx/effects';
import {
	RouterStateSerializer,
	StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';

import { SCHEDULER_AI_CRON_GENERATOR } from '@framework/analytics/components/scheduler/scheduler-ai-cron.port';
import { ANALYTICS_CARD_DATA_REF_ORIGIN } from '@framework/analytics/services/analytics-card-data-ref.service';
import { CoreModule } from '@jepto/core/core.module';
import { InAppOverlayContainerService } from '@jepto/core/services/in-app-overlay-container.service';
import { SentryUserService } from '@jepto/core/services/sentry-user.service';
import { environment } from '@jepto/environments/environment';
import { ClientEffects } from '@jepto/features/portfolio/store/clients/effects/clients.effect';
import * as fromPortfolioReducersAsFeature from '@jepto/features/portfolio/store/index';
import { provideJeptoChartEditorPaletteHost } from '@jepto/features/reports/providers/chart-editor-palette-host.provider';
import * as fromAccountStoreConfig from '@jepto/features/settings/store/account/account-store.config';
import { AccountEffects as SettingsAccountEffects } from '@jepto/features/settings/store/account/effects/account.effect';
import * as fromSettingsReducers from '@jepto/features/settings/store/index';
import { LabelEffects } from '@jepto/features/settings/store/labels/effects/labels.effects';
import * as fromLabelReducerAsFeature from '@jepto/features/settings/store/labels/index';
import * as fromLabelsStoreConfig from '@jepto/features/settings/store/labels/labels-store.config';
import { JepTokenInterceptor } from '@jepto/shared/interceptor/jep-token.interceptor';
import { SelectivePreloadingStrategy } from '@jepto/shared/routing/selective-preloading.strategy';
import { CustomRouterStateSerializer } from '@jepto/shared/utils/custom-route-serializer';
import { SchedulerAiCronAppSyncService } from '@jepto/shared/services/scheduler-ai-cron-appsync/scheduler-ai-cron-appsync.service';
import { registerChunkLoadGlobalHandlers } from '@jepto/shared/services/jep-error-handler/chunk-load-error.util';
import { JepChainedErrorHandler } from '@jepto/shared/services/jep-error-handler/jep-chained-error-handler';
import { JepErrorHandlerService } from '@jepto/shared/services/jep-error-handler/jep-error-handler.service';
import { AccountConnectionEffects } from '@jepto/shared/store/account-connections/effects/account-connection.effect';
import {
	AccountConnectionReducer,
	AccountConnectionsReducer,
} from '@jepto/shared/store/account-connections/reducers/account-connection.reducer';
import { AccountEffects } from '@jepto/shared/store/account/effects/account.effect';
import { ClientConnectionsEffects } from '@jepto/shared/store/client-connections/effects/client-connections.effect';
import { RouterEffects } from '@jepto/shared/store/router/effects/router.effects';
import { SharedUsersEffects } from '@jepto/shared/store/users/effects/users.effect';
import * as fromSharedReducers from '@jepto/shared/store/index';
import { AccountsReducer } from '@jepto/shared/store/account/reducers/account.reducer';

import { appRoutes } from './app.routing';
import { ReportModeReuseStrategy } from './core/strategies/report-mode-reuse.strategy';
import { SubscriptionEffects } from './features/admin/store/subscription/effects/subscription.effect';

/**
 * The canonical, live application config: main.ts bootstraps
 * `bootstrapApplication(AppComponent, appConfig)` (JEP-10520). It is a
 * one-to-one port of the former AppModule; keep the two concerns below in mind
 * when editing.
 *
 * - Change detection is zoneless (JEP-10521). Async callbacks that write
 *   template state must schedule CD themselves (signals, async pipe,
 *   `markForCheckOnEmit` / `markForCheck()`); nothing re-renders on its own.
 * - `CoreModule` (and the `SharedModule` it imports) is provided both here, for
 *   the environment injector routes/effects/guards resolve from, and via
 *   AppComponent's standalone `imports`, which gives the root component's view
 *   tree (the layout) its own instances of those NgModule-provided services.
 *   Prefer `providedIn: 'root'` for new services so they stay singletons.
 */
export const appConfig: ApplicationConfig = {
	providers: [
		provideZonelessChangeDetection(),
		// zone.js used to route errors thrown in async callbacks (timers,
		// promises, listeners) to the ErrorHandler; zoneless needs this to keep
		// JepChainedErrorHandler (Sentry + chunk-reload prompt) seeing them.
		provideBrowserGlobalErrorListeners(),

		provideRouter(
			appRoutes,
			withPreloading(SelectivePreloadingStrategy),
			withRouterConfig({ paramsInheritanceStrategy: 'always' })
		),
		// Reuse report component when switching between edit/preview modes
		// This prevents the skeleton loader from appearing during mode toggle
		{
			provide: RouteReuseStrategy,
			useClass: ReportModeReuseStrategy,
		},

		// Angular's default registrationStrategy ('registerWhenStable:30000'),
		// same as the former ServiceWorkerModule.register() (JEP-10472).
		provideServiceWorker('/ngsw-worker.js', {
			enabled: environment.production || environment.staging,
		}),

		provideAnimations(),
		provideHttpClient(withXhr()),

		// Kept as NgModules (not provideStore/provideEffects): SharedModule and the
		// lazy feature modules use StoreModule.forFeature/EffectsModule.forFeature,
		// which require StoreRootModule/EffectsRootModule. Same order as the
		// former AppModule imports.
		importProvidersFrom(
			CoreModule.forRoot(),
			StoreModule.forRoot({
				account: AccountsReducer,
				accountConnection: AccountConnectionReducer,
				accountConnections: AccountConnectionsReducer,
			}),
			// Moved off SharedModule so they register once, not once per lazy
			// module that imports it (JEP-10532).
			StoreModule.forFeature(
				'shared',
				fromSharedReducers.SharedModuleActionReducers
			),
			StoreRouterConnectingModule.forRoot({
				stateKey: 'router',
			}),
			StoreModule.forFeature(
				'portfolio',
				fromPortfolioReducersAsFeature.PortfolioModuleActionReducers
			),
			StoreModule.forFeature(
				'settings',
				fromSettingsReducers.SettingsModuleActionReducers
			),
			StoreModule.forFeature(
				'accountStore',
				fromAccountStoreConfig.AccountStoreActionReducers
			),
			StoreModule.forFeature(
				'labelsStore',
				fromLabelsStoreConfig.LabelsStoreActionReducers
			),
			StoreModule.forFeature(
				'label',
				fromLabelReducerAsFeature.LabelModuleActionReducers
			),
			EffectsModule.forRoot([
				AccountEffects,
				AccountConnectionEffects,
				// Were registered per lazy module via SharedModule (JEP-10532).
				RouterEffects,
				SharedUsersEffects,
				ClientConnectionsEffects,
				ClientEffects,
				SubscriptionEffects,
				SettingsAccountEffects,
				LabelEffects,
			]),
			environment.production
				? []
				: StoreDevtoolsModule.instrument({ name: 'JEPTO DevTools' }),
			CubeClientModule.forRoot({
				token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpYXQiOjE2NTgzODgxNTF9.stAzfzLSBaNT96UmfYdNyDHbp8H_jwGxfJfSuUrfDD0',
				options: {
					apiUrl: 'https://appalling-swallow.aws-us-west-2.cubecloudapp.dev/cubejs-api/v1',
				},
			})
		),

		// StoreRouterConnectingModule resolves this from the root injector; the
		// SharedModule copy only covered the lazy ones (JEP-10532).
		{
			provide: RouterStateSerializer,
			useClass: CustomRouterStateSerializer,
		},

		provideJeptoChartEditorPaletteHost(),
		{
			provide: ErrorHandler,
			useClass: JepChainedErrorHandler,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: (errorHandler: JepErrorHandlerService) => () => {
				registerChunkLoadGlobalHandlers(error =>
					errorHandler.handleError(error)
				);
			},
			deps: [JepErrorHandlerService],
			multi: true,
		},
		// Sentry tracing and user context providers — only when Sentry is enabled.
		// When disabled (local dev), TraceService is not instantiated and no Sentry calls are made.
		...(environment.sentry?.enabled
			? [
					{ provide: Sentry.TraceService, deps: [Router] },
					{
						provide: APP_INITIALIZER,
						useFactory: () => () => {},
						deps: [Sentry.TraceService],
						multi: true,
					},
					{
						provide: APP_INITIALIZER,
						useFactory: (sentryUser: SentryUserService) => () => {
							sentryUser.setUserContext(); // Fire-and-forget: don't block bootstrap
						},
						deps: [SentryUserService],
						multi: true,
					},
				]
			: []),
		{
			provide: OVERLAY_DEFAULT_CONFIG,
			useValue: { usePopover: false }, // Reverts to legacy z-index behavior
		},
		{
			provide: OverlayContainer,
			useClass: InAppOverlayContainerService,
		},
		// Same singleton as above — consumers may inject either token.
		{
			provide: InAppOverlayContainerService,
			useExisting: OverlayContainer,
		},
		{
			provide: SCHEDULER_AI_CRON_GENERATOR,
			useExisting: SchedulerAiCronAppSyncService,
		},
		{
			provide: ANALYTICS_CARD_DATA_REF_ORIGIN,
			useValue: environment.hostName,
		},
		JepTokenInterceptor,
		Title,
	],
};
